// The chat surface for the active agent: scrolling transcript that runs full
// height behind the overlay header, with the composer floating as an overlay
// at the bottom. Consecutive thinking/tool frames group into a quiet
// ProcessingChain. Older history pages in: first the in-memory window
// expands, then `fetch_earlier` pulls from the server.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ErrorPayload, NiuMessage } from '../../protocol/types';
import { useChatStore } from '../../store/chatStore';
import { AlertIcon, ArrowDownIcon, CloseIcon } from '../ui/Icons';
import { Spinner } from '../ui/Spinner';
import { Header } from '../layout/Header';
import { AssistantMessage } from './AssistantMessage';
import { Composer } from './Composer';
import { InlineSnippetPanel } from './InlineSnippetPanel';
import { ProcessingChain } from './ProcessingChain';
import { QuestionCard } from './QuestionCard';
import { StatusStrip } from './StatusStrip';
import { StreamingPreview } from './StreamingPreview';
import { UserMessage } from './UserMessage';

type MessageGroup =
  | { type: 'single'; message: NiuMessage; key: string }
  | { type: 'processing'; messages: NiuMessage[]; key: string };

const PROCESSING_KINDS = new Set(['thinking', 'tool_call', 'tool_result']);
const PAGE = 200;

function groupMessages(messages: NiuMessage[]): MessageGroup[] {
  const groups: MessageGroup[] = [];
  let buffer: NiuMessage[] = [];
  const flush = () => {
    if (buffer.length > 0) {
      groups.push({ type: 'processing', messages: buffer, key: `proc-${buffer[0]!.id}` });
      buffer = [];
    }
  };
  for (const msg of messages) {
    if (PROCESSING_KINDS.has(msg.payload.kind)) {
      buffer.push(msg);
    } else {
      flush();
      groups.push({ type: 'single', message: msg, key: msg.id });
    }
  }
  flush();
  return groups;
}

interface ChatViewProps {
  onOpenSidebar: () => void;
  onOpenSettings: () => void;
}

export function ChatView({ onOpenSidebar, onOpenSettings }: ChatViewProps) {
  const activeAgentId = useChatStore((s) => s.activeAgentId);
  const chat = useChatStore((s) => s.chats[activeAgentId]);
  const retry = useChatStore((s) => s.retryUserMessage);
  const answer = useChatStore((s) => s.answerQuestion);
  const clearError = useChatStore((s) => s.clearError);
  const loadOlderMessages = useChatStore((s) => s.loadOlderMessages);
  const closeInlineSnippet = useChatStore((s) => s.closeInlineSnippet);

  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  // Mirror of `atBottom` for the ResizeObserver, which holds a stale closure.
  const atBottomRef = useRef(true);
  const [displayCount, setDisplayCount] = useState(PAGE);

  const allMessages = chat?.history.messages ?? [];
  const hasMore = allMessages.length > displayCount;
  const messages = hasMore ? allMessages.slice(-displayCount) : allMessages;
  const canFetchEarlier =
    !!chat && !chat.reachedOldestRestored && allMessages.some((m) => m.seq != null);
  const showLoadMore = (hasMore || canFetchEarlier) && allMessages.length > 0;
  const isLoadingOlder = chat?.loadingOlder ?? false;

  const groups = useMemo(() => groupMessages(messages), [messages]);
  const streamingBlocks = useMemo(
    () => Object.values(chat?.streamingBlocks ?? {}),
    [chat?.streamingBlocks],
  );
  // Sum of revealed characters — auto-scroll trigger that follows the
  // typewriter as text grows, not just when block count changes.
  const streamingTick = useMemo(
    () => streamingBlocks.reduce((n, b) => n + b.displayedLen, 0),
    [streamingBlocks],
  );

  const statusState = chat?.status?.state;
  const pendingQuestion = chat?.pendingQuestion ?? null;
  const answeredValue = pendingQuestion ? chat?.history.answers[pendingQuestion.questionId] : undefined;

  // Keep the view pinned to the bottom while the user hasn't scrolled away.
  useLayoutEffect(() => {
    if (atBottom) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, streamingBlocks.length, streamingTick, statusState, pendingQuestion?.questionId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const next = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      atBottomRef.current = next;
      setAtBottom(next);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Re-pin to the exact bottom on any transcript height change — covers async
  // growth the layout effect's deps miss (a tool body expanding, a processing
  // chain animating open). Stays detached once the user has scrolled away.
  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content) return;
    const ro = new ResizeObserver(() => {
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, []);

  // On agent switch, reset paging and jump to the bottom.
  useEffect(() => {
    setDisplayCount(PAGE);
    setAtBottom(true);
    atBottomRef.current = true;
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, [activeAgentId]);

  function loadMore() {
    const el = scrollRef.current;
    const prevHeight = el?.scrollHeight ?? 0;
    const prevTop = el?.scrollTop ?? 0;
    if (hasMore) {
      setDisplayCount((c) => c + PAGE);
    } else if (canFetchEarlier && !isLoadingOlder) {
      loadOlderMessages(activeAgentId);
    }
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (el) el.scrollTop = prevTop + (el.scrollHeight - prevHeight);
      }),
    );
  }

  const empty = messages.length === 0 && streamingBlocks.length === 0;
  const inlineSnippet = chat?.inlineSnippet ?? null;

  // On a phone the split pane can't show both columns at a usable width, so an
  // open snippet gets its own full-viewport view the user flips to and from. A
  // freshly opened snippet (new token) takes over; reload frames keep the same
  // token, so they don't yank someone who has flipped back to the transcript.
  const [mobileSnippetView, setMobileSnippetView] = useState<'chat' | 'snippet'>('snippet');
  useEffect(() => {
    if (inlineSnippet) setMobileSnippetView('snippet');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineSnippet?.token]);

  // The transcript + composer occupy the left column; an inline snippet, when
  // present, is the right column. Desktop (`md:`) animates a 1fr/1fr split;
  // mobile has no room for two columns, so it collapses to whichever single
  // column `mobileSnippetView` selects and animates the swap.
  const snippetCols = !inlineSnippet
    ? 'grid-cols-[1fr_0fr]'
    : mobileSnippetView === 'snippet'
      ? 'grid-cols-[0fr_1fr] md:grid-cols-[1fr_1fr]'
      : 'grid-cols-[1fr_0fr] md:grid-cols-[1fr_1fr]';
  return (
    <div
      className={`grid h-full bg-background transition-[grid-template-columns] duration-300 ease-out ${snippetCols}`}
    >
      <div className="relative h-full min-w-0 overflow-hidden">
        <div ref={scrollRef} className="scrollbar-thin h-full overflow-y-auto overflow-x-hidden">
          <div
            ref={contentRef}
            className="mx-auto flex max-w-[var(--chat-max-width)] flex-col gap-4 px-4 pb-44 pt-24"
          >
            {showLoadMore ? (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isLoadingOlder}
                  className="flex items-center gap-1.5 rounded-full bg-elevated px-4 py-1.5 text-xs text-text-secondary transition-colors hover:text-text-primary disabled:opacity-60"
                >
                  {isLoadingOlder ? <Spinner size={12} /> : null}
                  {isLoadingOlder
                    ? 'Loading older messages…'
                    : hasMore
                      ? `Load ${Math.min(PAGE, allMessages.length - displayCount)} older messages`
                      : 'Load older from server'}
                </button>
              </div>
            ) : null}

            {empty ? <EmptyState /> : null}

            {groups.map((g) =>
              g.type === 'processing' ? (
                <ProcessingChain key={g.key} messages={g.messages} />
              ) : g.message.payload.kind === 'user_message' ? (
                <UserMessage
                  key={g.key}
                  payload={g.message.payload.data}
                  timestamp={chat?.history.timestamps[g.message.id]}
                  status={chat?.history.deliveryStatus[g.message.id]}
                  reactions={chat?.history.reactions[g.message.id]}
                  onRetry={() => void retry(g.message.id)}
                />
              ) : g.message.payload.kind === 'assistant_message' ? (
                <AssistantMessage
                  key={g.key}
                  payload={g.message.payload.data}
                  timestamp={chat?.history.timestamps[g.message.id]}
                />
              ) : null,
            )}

            <StreamingPreview blocks={streamingBlocks} />

            {pendingQuestion ? (
              <QuestionCard
                question={pendingQuestion}
                {...(answeredValue !== undefined ? { answeredValue } : {})}
                onSelect={(value) => answer(pendingQuestion.questionId, value)}
              />
            ) : null}

            {chat?.lastError ? (
              <ErrorBanner error={chat.lastError} onDismiss={() => clearError(activeAgentId)} />
            ) : null}
          </div>
        </div>

        {/* Composer overlay — floats over the transcript. The scroll-to-bottom
            button rides just above it (`bottom-full`) so it tracks the
            composer's real height — status strip, attachment chips — instead
            of a fixed offset that drifts over the composer on small screens. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 px-3 pb-3">
          <div className="pointer-events-auto relative mx-auto max-w-[var(--chat-max-width)]">
            <button
              type="button"
              onClick={() => {
                const el = scrollRef.current;
                if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
              }}
              aria-label="Scroll to bottom"
              className={`absolute bottom-full right-0 mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-surface text-text-primary shadow-md ring-1 ring-divider/60 transition-all ${
                atBottom ? 'pointer-events-none translate-y-1 opacity-0' : 'opacity-100'
              }`}
            >
              <ArrowDownIcon size={16} />
            </button>
            <StatusStrip status={chat?.status ?? null} />
            <Composer />
          </div>
        </div>

        {/* Fade backdrop + header — scoped to the chat column so they never
            bleed over an open inline snippet (which has its own header). */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-24 bg-gradient-to-b from-background via-background/80 to-transparent"
        />
        <Header
          onOpenSidebar={onOpenSidebar}
          onOpenSettings={onOpenSettings}
          snippetTitle={inlineSnippet?.title ?? null}
          onShowSnippet={() => setMobileSnippetView('snippet')}
        />
      </div>

      <div
        className={`h-full min-w-0 overflow-hidden ${inlineSnippet ? 'border-l border-divider' : ''}`}
      >
        {inlineSnippet ? (
          <InlineSnippetPanel
            snippet={inlineSnippet}
            reloadNonce={chat?.inlineSnippetReloadNonce ?? 0}
            onClose={() => closeInlineSnippet(activeAgentId)}
            onShowChat={() => setMobileSnippetView('chat')}
          />
        ) : null}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-24 text-center text-text-secondary">
      <div className="text-4xl">🪐</div>
      <div className="text-base font-medium text-text-primary">Niu</div>
      <div className="text-sm">Send a message to get started.</div>
    </div>
  );
}

function ErrorBanner({ error, onDismiss }: { error: ErrorPayload; onDismiss: () => void }) {
  return (
    <div className="flex items-start gap-2 rounded-xl bg-error-bg px-3 py-2 text-error-text">
      <AlertIcon size={16} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">Error{error.code ? ` (${error.code})` : ''}</div>
        <div className="whitespace-pre-wrap break-words text-xs">{error.message ?? 'Unknown error'}</div>
      </div>
      <button type="button" onClick={onDismiss} aria-label="Dismiss" className="opacity-70 hover:opacity-100">
        <CloseIcon size={15} />
      </button>
    </div>
  );
}
